import {
    MutableRefObject,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { CheckboxItems } from '@own/fds-react/dist/types/checkbox.types';
import { BookmarkDataResponse } from '../../../models/bookmark-data';
import { DyfFeatureGuideContentProps } from '../../../views/dyf-landing-view/hooks/use-dyf-feature-guide-content';
import { BookmarkContext } from '../../../views/dyf-landing-view/context/dyf-bookmark-context';
import { useAnalytics } from '../../use-analytics';
import { UiFeature } from '../use-your-vehicle-features/vehicle-features.types';
import { useYourVehicleFeaturesAndCategories } from '../use-your-vehicle-features/use-your-vehicle-features';
import { extractCategories } from '../use-your-vehicle-features/category-utils';

function filterCategoryFeatures(
    filterType: 'all' | 'bookmark' | 'category',
    categories?: string[],
    allFeatures?: UiFeature[],
    bookmarksData?: BookmarkDataResponse
): UiFeature[] {
    if (!allFeatures) {
        return [];
    }
    if (filterType === 'all') {
        return allFeatures;
    } else if (filterType === 'bookmark' && bookmarksData) {
        return allFeatures.filter(feature =>
            bookmarksData.content.some(bookmark => {
                return (
                    bookmark.personalizationValueOne &&
                    feature.topicId &&
                    parseInt(bookmark.personalizationValueOne) ===
                        parseInt(feature.topicId.toString())
                );
            })
        );
    } else if (filterType === 'category' && categories) {
        return categories.length > 0
            ? allFeatures.filter(article =>
                  categories.some(category =>
                      article.categoryNames?.includes(category)
                  )
              )
            : allFeatures;
    }
    return allFeatures;
}

interface UseCheckboxesProps {
    featureGuideContent: DyfFeatureGuideContentProps;
}

export interface CheckboxHookState {
    loading: boolean;
    visibleFeatures: UiFeature[];
    allFeaturesCheckbox: CheckboxItems;
    myBookmarksCheckbox: CheckboxItems;
    selectedFeatureCategories: string[];
}

export interface UseCheckboxesHook extends CheckboxHookState {
    allCategories: MutableRefObject<CheckboxItems[]>;
    disabledCategories: CheckboxItems[];
    controlCheckBoxes: (
        type: 'all' | 'bookmark' | 'category',
        value: boolean,
        uncheck?: boolean
    ) => void;
}

export const useCheckboxCategoriesAndFeatures = (
    props: UseCheckboxesProps
): UseCheckboxesHook => {
    const { featureGuideContent } = props;

    const {
        authorizedFeatures,
        loading: vehicleFeaturesLoading,
        hiddenCategories,
    } = useYourVehicleFeaturesAndCategories();

    const { bookmarksData } = useContext(BookmarkContext);

    const [fireEvents] = useAnalytics();

    const [
        {
            visibleFeatures,
            allFeaturesCheckbox,
            selectedFeatureCategories,
            myBookmarksCheckbox,
            loading,
        },
        setState,
    ] = useState<CheckboxHookState>({
        loading: true,
        visibleFeatures: [],
        selectedFeatureCategories: [],
        myBookmarksCheckbox: {
            name: featureGuideContent.myBookmarksCheckbox,
            value: featureGuideContent.myBookmarksCheckbox,
            ariaLabel: featureGuideContent.myBookmarksCheckbox,
            itemName: featureGuideContent.myBookmarksCheckbox,
            onClick: handleCheckboxClick,
            checked: false,
        },
        allFeaturesCheckbox: {
            name: featureGuideContent.allFeaturesCheckbox,
            value: featureGuideContent.allFeaturesCheckbox,
            ariaLabel: featureGuideContent.allFeaturesCheckbox,
            itemName: featureGuideContent.allFeaturesCheckbox,
            onClick: handleCheckboxClick,
            checked: true,
        },
    });

    const allCategories = useRef<CheckboxItems[]>([]);
    const allFeaturesRef = useRef<UiFeature[]>([]);

    const checkAllFeaturesCheckBox = (value: boolean) =>
        setState(prev => ({
            ...prev,
            allFeaturesCheckbox: {
                ...prev.allFeaturesCheckbox,
                checked: value,
            },
        }));

    const checkBookmarkCheckBox = (value: boolean) =>
        setState(prev => ({
            ...prev,
            myBookmarksCheckbox: {
                ...prev.myBookmarksCheckbox,
                checked: value,
            },
        }));

    useEffect(() => {
        setState(prev => ({ ...prev, loading: vehicleFeaturesLoading }));
        if (!vehicleFeaturesLoading && authorizedFeatures) {
            initializeCategories(authorizedFeatures);
        }
    }, [vehicleFeaturesLoading, authorizedFeatures]);

    useEffect(() => {
        if (allFeaturesCheckbox.checked) {
            setState(prev => ({
                ...prev,
                visibleFeatures: filterCategoryFeatures(
                    'all',
                    [],
                    allFeaturesRef.current,
                    bookmarksData
                ),
            }));
        } else if (selectedFeatureCategories.length > 0) {
            setState(prev => ({
                ...prev,
                visibleFeatures: filterCategoryFeatures(
                    'category',
                    selectedFeatureCategories,
                    allFeaturesRef.current,
                    bookmarksData
                ),
            }));
        }
    }, [selectedFeatureCategories, allFeaturesCheckbox.checked]);

    useEffect(() => {
        if (
            selectedFeatureCategories.length == 0 &&
            !allFeaturesCheckbox.checked &&
            !myBookmarksCheckbox.checked
        ) {
            controlCheckBoxes('all', true);
        }
    }, [
        selectedFeatureCategories,
        myBookmarksCheckbox.checked,
        allFeaturesCheckbox.checked,
    ]);

    useEffect(() => {
        if (myBookmarksCheckbox.checked && bookmarksData) {
            const f = filterCategoryFeatures(
                'bookmark',
                [],
                allFeaturesRef.current,
                bookmarksData
            );

            setState(prev => ({
                ...prev,
                visibleFeatures: f,
            }));
        }
    }, [myBookmarksCheckbox.checked, bookmarksData]);

    const initializeCategories = useCallback(
        (features: UiFeature[]) => {
            if (features.length > 0) {
                allFeaturesRef.current = features;
                setState(prev => ({
                    ...prev,
                    visibleFeatures: features,
                }));

                const allCategoriesTmp = extractCategories(features).map(c => ({
                    ...c,
                    readonly: true,
                    onClick: handleCheckboxClick,
                }));

                allCategories.current = allCategoriesTmp;
            }
        },
        [handleCheckboxClick]
    );

    const unAuthCategoryBoxes = useMemo<CheckboxItems[]>(() => {
        if (hiddenCategories.length > 0) {
            return hiddenCategories.map(m => ({
                name: m.displayName,
                value: m.queryName,
                ariaLabel: m.displayName,
                itemName: m.displayName,
                onClick: () => null,
                checked: false,
                disabled: true,
            }));
        } else return [];
    }, [hiddenCategories]);

    function handleCheckboxClick(e: React.ChangeEvent<HTMLInputElement> | any) {
        const selectedValue = e.target.name;

        if (selectedValue === featureGuideContent.myBookmarksCheckbox) {
            clearAllCategories();
            if (e.target.checked) controlCheckBoxes('bookmark', true);
            else {
                controlCheckBoxes('all', true);
                controlCheckBoxes('bookmark', false);
            }
        } else if (selectedValue === featureGuideContent.allFeaturesCheckbox) {
            clearAllCategories();
            controlCheckBoxes('bookmark', false);
            controlCheckBoxes('all', true);
        } else {
            handleCategoryCheckboxChange(selectedValue);
        }

        fireEvents('dyf-fg-category-checkboxes', undefined, {
            categoryName: selectedValue,
        });
    }

    const setSelectedFeatureCategories = (value: string[]) => {
        setState(prev => ({ ...prev, selectedFeatureCategories: value }));
    };
    // Runs every time a category is clicked
    const handleCategoryCheckboxChange = (selectedValue: string) => {
        const thisCategoryIsChecked = selectedFeatureCategories.find(
            cat => cat === selectedValue
        );
        if (thisCategoryIsChecked && selectedFeatureCategories.length === 1) {
            controlCheckBoxes('all', true);
            return;
        } else {
            controlCheckBoxes('all', false);
            controlCheckBoxes('bookmark', false);
        }

        const tempSelectedCategories = [...allCategories.current];
        const catToCheck = tempSelectedCategories.findIndex(
            c => c.name === selectedValue
        );
        tempSelectedCategories[catToCheck].checked = !tempSelectedCategories[
            catToCheck
        ].checked;
        const filtered = tempSelectedCategories.filter(
            category => category.checked
        );

        setSelectedFeatureCategories(filtered.flatMap(f => f.name));
    };

    function clearAllCategories() {
        allCategories.current = allCategories.current.map(c => ({
            ...c,
            checked: false,
        }));
    }

    function controlCheckBoxes(
        type: 'all' | 'bookmark' | 'category',
        value: boolean
    ) {
        if (type === 'all') {
            checkAllFeaturesCheckBox(value);
            if (value) setSelectedFeatureCategories([]);
        } else if (type === 'bookmark') {
            checkBookmarkCheckBox(value);
            if (value) {
                checkAllFeaturesCheckBox(false);
            }
        }
    }

    return {
        allCategories,
        selectedFeatureCategories,
        visibleFeatures,
        allFeaturesCheckbox,
        myBookmarksCheckbox,
        controlCheckBoxes,
        disabledCategories: unAuthCategoryBoxes,
        loading,
    };
};
